/**
 * Módulo de impressão direta via Web Bluetooth API para impressoras térmicas.
 * Suporta protocolo ESC/POS (texto raw) — sem diálogo de impressão.
 *
 * Serviços BLE suportados (tentados em ordem):
 *   1. 000018f0-0000-1000-8000-00805f9b34fb  — serviço genérico impressora térmica
 *      Characterística write: 00002af1-0000-1000-8000-00805f9b34fb
 *   2. 49535343-fe7d-4ae5-8fa9-9fafd205e455  — Nordic UART / BLE Serial
 *      Characterística TX:   49535343-1e4d-4bd9-ba61-23c647249616
 *   3. 0000ff00-0000-1000-8000-00805f9b34fb  — serviço alternativo comum
 *      Characterística write: 0000ff02-0000-1000-8000-00805f9b34fb
 *
 * Uso:
 *   import { bluetoothPrinter } from './bluetoothPrinter'
 *   await bluetoothPrinter.connect()
 *   await bluetoothPrinter.print(text)
 */

// ── Tipos Web Bluetooth (não incluídos no lib DOM padrão do TS) ───────────────

interface BluetoothRemoteGATTCharacteristic {
  writeValue(value: BufferSource): Promise<void>
  writeValueWithResponse?(value: BufferSource): Promise<void>
  writeValueWithoutResponse?(value: BufferSource): Promise<void>
}

interface BluetoothRemoteGATTService {
  getCharacteristic(characteristic: string): Promise<BluetoothRemoteGATTCharacteristic>
}

interface BluetoothRemoteGATTServer {
  connected: boolean
  connect(): Promise<BluetoothRemoteGATTServer>
  disconnect(): void
  getPrimaryService(service: string): Promise<BluetoothRemoteGATTService>
}

interface BluetoothDevice extends EventTarget {
  readonly name?: string
  readonly id: string
  readonly gatt?: BluetoothRemoteGATTServer
  addEventListener(type: 'gattserverdisconnected', listener: EventListenerOrEventListenerObject): void
}

interface RequestDeviceOptions {
  filters?: Array<{ services?: string[]; name?: string; namePrefix?: string }>
  acceptAllDevices?: boolean
  optionalServices?: string[]
}

interface Bluetooth {
  requestDevice(options: RequestDeviceOptions): Promise<BluetoothDevice>
  getAvailability(): Promise<boolean>
}

// Constantes de serviço/characteristic BLE
const SERVICES = [
  {
    service:        '000018f0-0000-1000-8000-00805f9b34fb',
    characteristic: '00002af1-0000-1000-8000-00805f9b34fb',
  },
  {
    service:        '49535343-fe7d-4ae5-8fa9-9fafd205e455',
    characteristic: '49535343-1e4d-4bd9-ba61-23c647249616',
  },
  {
    service:        '0000ff00-0000-1000-8000-00805f9b34fb',
    characteristic: '0000ff02-0000-1000-8000-00805f9b34fb',
  },
]

const STORAGE_KEY = 'bt_printer_device_name'

// Comandos ESC/POS
export const ESC = 0x1b
export const GS  = 0x1d

export const CMD = {
  INIT:          [ESC, 0x40],              // ESC @ — inicializar
  ALIGN_LEFT:    [ESC, 0x61, 0x00],        // ESC a 0
  ALIGN_CENTER:  [ESC, 0x61, 0x01],        // ESC a 1
  ALIGN_RIGHT:   [ESC, 0x61, 0x02],        // ESC a 2
  BOLD_ON:       [ESC, 0x45, 0x01],        // ESC E 1
  BOLD_OFF:      [ESC, 0x45, 0x00],        // ESC E 0
  DOUBLE_SIZE:   [ESC, 0x21, 0x30],        // ESC ! — double width+height
  NORMAL_SIZE:   [ESC, 0x21, 0x00],        // ESC ! — normal
  FEED_3:        [ESC, 0x64, 0x03],        // ESC d 3 — avança 3 linhas
  CUT:           [GS,  0x56, 0x00],        // GS V 0 — corta (se suportado)
  LF:            [0x0a],                   // \n
}

// Codificação texto → Uint8Array (CP437/Latin1 adequado para térmicas)
function encodeText(text: string): Uint8Array {
  const bytes: number[] = []
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i)
    // Substitui caracteres acentuados comuns por equivalente ASCII
    const sub: Record<string, string> = {
      'á':'a','à':'a','ã':'a','â':'a','ä':'a',
      'é':'e','è':'e','ê':'e','ë':'e',
      'í':'i','ì':'i','î':'i','ï':'i',
      'ó':'o','ò':'o','õ':'o','ô':'o','ö':'o',
      'ú':'u','ù':'u','û':'u','ü':'u',
      'ç':'c','ñ':'n',
      'Á':'A','À':'A','Ã':'A','Â':'A','Ä':'A',
      'É':'E','È':'E','Ê':'E','Ë':'E',
      'Í':'I','Ì':'I','Î':'I','Ï':'I',
      'Ó':'O','Ò':'O','Õ':'O','Ô':'O','Ö':'O',
      'Ú':'U','Ù':'U','Û':'U','Ü':'U',
      'Ç':'C','Ñ':'N',
    }
    const ch = text[i]
    if (sub[ch]) {
      bytes.push(sub[ch].charCodeAt(0))
    } else if (c < 256) {
      bytes.push(c)
    } else {
      bytes.push(0x3f) // '?'
    }
  }
  return new Uint8Array(bytes)
}

// Monta buffer de bytes a partir de arrays de comandos e strings
function buildBuffer(...parts: Array<number[] | string | Uint8Array>): Uint8Array {
  const chunks: Uint8Array[] = parts.map(p => {
    if (typeof p === 'string') return encodeText(p)
    if (p instanceof Uint8Array) return p
    return new Uint8Array(p)
  })
  const total = chunks.reduce((s, c) => s + c.length, 0)
  const buf = new Uint8Array(total)
  let offset = 0
  for (const c of chunks) {
    buf.set(c, offset)
    offset += c.length
  }
  return buf
}

// Tipo de dados para impressão de resultado
export interface PrintResultadoOpts {
  atirador_nome: string
  modalidade_nome: string
  grupo: 'Precisao' | 'Silhueta' | string
  armaDistancia?: string
  // Precisão
  qtd_x?: number; qtd_10?: number; qtd_9?: number; qtd_8?: number
  qtd_7?: number; qtd_6?: number; qtd_5?: number; qtd_4?: number
  qtd_3?: number; qtd_2?: number; qtd_1?: number; qtd_0?: number
  totalPontos?: number
  totalPenalidades?: number
  // Silhueta
  galinha?: number; porco?: number; peru?: number; carneiro?: number
  totalAcertos?: number
}

// Formata cupom ESC/POS para resultado de tiro
export function formatEscPosResultado(opts: PrintResultadoOpts): Uint8Array {
  const LINE = '\n'
  const SEP_DASHES = '--------------------------------' + LINE  // 32 chars ~58mm / ajustar
  const SEP_EQUALS = '================================' + LINE

  const parts: Array<number[] | string | Uint8Array> = []

  const push = (...p: Array<number[] | string>) => parts.push(...p)

  // Init
  push(CMD.INIT)

  // Cabeçalho
  push(CMD.ALIGN_CENTER, CMD.BOLD_ON)
  push('CAMPEONATO DE TIRO' + LINE)
  push(CMD.BOLD_OFF)
  push(SEP_EQUALS)

  // Nome do atirador
  push(CMD.ALIGN_CENTER, CMD.BOLD_ON)
  push(opts.atirador_nome.toUpperCase() + LINE)
  push(CMD.BOLD_OFF)

  // Modalidade
  push(CMD.ALIGN_CENTER, CMD.BOLD_ON)
  push(opts.modalidade_nome.toUpperCase() + LINE)
  push(CMD.BOLD_OFF)

  // Arma/distância
  if (opts.armaDistancia) {
    push(CMD.ALIGN_CENTER)
    push(opts.armaDistancia.toUpperCase() + LINE)
  }

  push(CMD.ALIGN_LEFT)
  push(SEP_DASHES)

  if (opts.grupo === 'Precisao') {
    // Título série
    push(CMD.ALIGN_CENTER, CMD.BOLD_ON)
    push('SERIE 1' + LINE)
    push(CMD.BOLD_OFF, CMD.ALIGN_LEFT)

    // Grid 4 colunas — formatado como texto
    const row = (cells: string[]) => cells.map(c => c.padEnd(8)).join('') + LINE
    push(row([`X=${opts.qtd_x ?? 0}`, `10=${opts.qtd_10 ?? 0}`, `9=${opts.qtd_9 ?? 0}`, `8=${opts.qtd_8 ?? 0}`]))
    push(row([`7=${opts.qtd_7 ?? 0}`, `6=${opts.qtd_6 ?? 0}`, `5=${opts.qtd_5 ?? 0}`, `4=${opts.qtd_4 ?? 0}`]))
    push(row([`3=${opts.qtd_3 ?? 0}`, `2=${opts.qtd_2 ?? 0}`, `1=${opts.qtd_1 ?? 0}`, `0=${opts.qtd_0 ?? 0}`]))

    push(SEP_DASHES)

    // Totais
    push(CMD.BOLD_ON)
    push(`TOTAL DE PENALIDADES = ${opts.totalPenalidades ?? opts.qtd_0 ?? 0}` + LINE)
    push(`TOTAL DE PONTOS = ${opts.totalPontos ?? 0}` + LINE)
    push(CMD.BOLD_OFF)
  } else if (opts.grupo === 'Silhueta') {
    push(CMD.ALIGN_LEFT)
    push(`GALINHA = ${opts.galinha ?? 0}` + LINE)
    push(`PORCO = ${opts.porco ?? 0}` + LINE)
    push(`PERU = ${opts.peru ?? 0}` + LINE)
    push(`CARNEIRO = ${opts.carneiro ?? 0}` + LINE)

    push(SEP_DASHES)

    push(CMD.BOLD_ON)
    push(`PONTOS = ${opts.totalAcertos ?? 0}` + LINE)
    push(CMD.BOLD_OFF)
  }

  push(SEP_DASHES)

  // Data/hora
  push(CMD.ALIGN_CENTER)
  push(new Date().toLocaleString('pt-BR') + LINE)

  // Assinatura
  push(CMD.ALIGN_LEFT)
  push(LINE)
  push('Assinatura: ____________________________' + LINE)
  push(LINE)

  // Feed e corte
  push(CMD.FEED_3)
  push(CMD.CUT)

  return buildBuffer(...parts)
}

// ── Classe principal ─────────────────────────────────────────────────────────

class BluetoothPrinterService {
  private device: BluetoothDevice | null = null
  private characteristic: BluetoothRemoteGATTCharacteristic | null = null
  private _connecting = false

  /** true se a API Web Bluetooth está disponível no browser */
  isSupported(): boolean {
    return typeof navigator !== 'undefined' && 'bluetooth' in navigator
  }

  /** true se há uma conexão ativa com a impressora */
  isConnected(): boolean {
    return !!(this.device?.gatt?.connected && this.characteristic)
  }

  /** Nome do dispositivo salvo no localStorage (pode não estar conectado) */
  savedDeviceName(): string | null {
    return localStorage.getItem(STORAGE_KEY)
  }

  /**
   * Solicita seleção de impressora Bluetooth ao usuário (requer gesto do usuário).
   * Após conectar, salva nome no localStorage para exibição futura.
   */
  async connect(): Promise<void> {
    if (!this.isSupported()) {
      throw new Error('Web Bluetooth não suportado neste navegador. Use Chrome/Edge mobile.')
    }
    if (this._connecting) return
    this._connecting = true

    try {
      // requestDevice precisa de gesto do usuário — ok pois vem de um clique
      const optionalServices = SERVICES.map(s => s.service)
      const device = await (navigator as any).bluetooth.requestDevice({
        // Aceita qualquer dispositivo Bluetooth (para não limitar filtros)
        acceptAllDevices: true,
        optionalServices,
      })

      await this._connectDevice(device)
      localStorage.setItem(STORAGE_KEY, device.name ?? 'Impressora Bluetooth')
    } finally {
      this._connecting = false
    }
  }

  /** Conecta a um BluetoothDevice já obtido (interno) */
  private async _connectDevice(device: BluetoothDevice): Promise<void> {
    this.device = device
    this.characteristic = null

    // Listener de desconexão para limpar estado
    device.addEventListener('gattserverdisconnected', () => {
      this.characteristic = null
    })

    const server = await device.gatt!.connect()

    // Tenta cada serviço em ordem até encontrar um que funcione
    for (const { service, characteristic } of SERVICES) {
      try {
        const svc = await server.getPrimaryService(service)
        const chr = await svc.getCharacteristic(characteristic)
        this.characteristic = chr
        return
      } catch {
        // tenta próximo serviço
      }
    }

    throw new Error(
      'Impressora conectada mas nenhum serviço de impressão ESC/POS foi encontrado.\n' +
      'Verifique se é uma impressora térmica BLE compatível.'
    )
  }

  /** Desconecta da impressora atual */
  disconnect(): void {
    if (this.device?.gatt?.connected) {
      this.device.gatt.disconnect()
    }
    this.device = null
    this.characteristic = null
    localStorage.removeItem(STORAGE_KEY)
  }

  /**
   * Envia buffer ESC/POS para a impressora.
   * Se não estiver conectado, tenta reconectar ao dispositivo atual.
   * @param data Buffer de bytes ou string simples
   */
  async print(data: Uint8Array | string): Promise<void> {
    const buf = typeof data === 'string' ? encodeText(data) : data

    // Reconecta se necessário (sem diálogo — reutiliza device já pareado)
    if (!this.isConnected()) {
      if (!this.device) {
        throw new Error('Nenhuma impressora conectada. Clique em "Conectar Impressora" primeiro.')
      }
      // Dispositivo já pareado — reconecta sem diálogo
      await this._connectDevice(this.device)
    }

    if (!this.characteristic) {
      throw new Error('Característica de escrita não disponível.')
    }

    // Envia em chunks pequenos para compatibilidade com impressoras térmicas BLE.
    // A maioria das impressoras aceita no máximo 20-100 bytes por write.
    // 512 bytes causava corte de cupom pois a impressora não consegue processar tudo.
    const CHUNK = 100
    const DELAY_MS = 120  // Delay entre chunks (ms) — evita overrun na fila BLE

    // Delay inicial após inicialização (ESC @) para a impressora se preparar
    await new Promise(r => setTimeout(r, 200))

    // Prefere writeValueWithResponse (garante ACK da impressora antes do próximo chunk)
    // Fallback: writeValueWithoutResponse ou writeValue
    const writeChunk = (
      this.characteristic.writeValueWithResponse?.bind(this.characteristic)
      ?? this.characteristic.writeValueWithoutResponse?.bind(this.characteristic)
      ?? this.characteristic.writeValue.bind(this.characteristic)
    )

    for (let offset = 0; offset < buf.length; offset += CHUNK) {
      const chunk = buf.slice(offset, offset + CHUNK)
      await writeChunk(chunk)
      // Pausa entre chunks para não sobrecarregar a fila BLE da impressora
      if (offset + CHUNK < buf.length) {
        await new Promise(r => setTimeout(r, DELAY_MS))
      }
    }
  }

  /** Imprime resultado de tiro formatado em ESC/POS */
  async printResultado(opts: PrintResultadoOpts): Promise<void> {
    const buf = formatEscPosResultado(opts)
    await this.print(buf)
  }
}

// Singleton exportado
export const bluetoothPrinter = new BluetoothPrinterService()
